import { useMemo, type ComponentProps } from 'react';

import { typographyVariants } from '@mint-lib/tw-components';
import { AvatarAi } from '@mint-lib/tw-components/ai';
import { cn } from '@mint-lib/tw-utils';

import { useAgents } from '../../../hooks/useAgents.jsx';
import { AgentType } from '../../../types/agents.js';

type Props = {
  agentName: string;
  avatarSize?: ComponentProps<typeof AvatarAi>['size'];
  className?: string;
  labelDirection?: 'row' | 'column';
  accordionView?: boolean;
  typographyClassName?: string;
};

const AgentLabel = ({
  agentName,
  accordionView,
  avatarSize,
  className,
  labelDirection = 'row',
  typographyClassName,
}: Props) => {
  const { fallbackAgent, findAgentByName } = useAgents();

  const aiAgent = useMemo((): AgentType => {
    return (
      findAgentByName(agentName) || fallbackAgent // AI Assistant
    );
  }, [agentName, fallbackAgent, findAgentByName]);

  return (
    <div
      className={cn(
        'flex flex-row items-center gap-2',
        accordionView ? 'h-full w-full' : 'w-auto',
        className,
      )}
      data-testid="agent-label"
    >
      <AvatarAi
        role={aiAgent.role}
        size={avatarSize}
        hasPointer={accordionView}
      />
      <div
        className={cn(
          'flex items-center',
          labelDirection === 'row' ? 'flex-row gap-2' : 'flex-col gap-0.5',
          accordionView ? 'w-full justify-between' : 'w-auto',
        )}
      >
        <p
          className={
            typographyClassName ??
            typographyVariants({
              variant: 'bodyShort02',
              color: 'secondary',
            })
          }
        >
          {aiAgent.label}
        </p>
      </div>
    </div>
  );
};

export { AgentLabel };
